'use client';

import { useIntl } from 'react-intl';
import { useFormContext } from 'react-hook-form';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { Card, CardHeader, CardContent } from '@packages/shared/src/ui/Card/Card';
import { SampleItem } from '../../components/SampleItem/SampleItem.client';
import { SampleOrderContent } from '../../components/SampleOrderContent/SampleOrderContent';
import { Counter } from '../../components/Counter/Counter';
import { PrimaryButton } from '../../components/PrimaryButton/PrimaryButton.client';
import { SecondaryButton } from '../../components/SecondaryButton/SecondaryButton.client';
import type { AbortHandler, SampleOrderData, SetStep, SampleOrderMode } from '../../types';
import type { SampleDataValuesSchema } from '../schema';
import { SampleOrderErrorCodes, maximumSamples, sampleDataValuesSchema } from '../schema';
import type { SampleEntry } from '../types';
import { SampleOrderSteps } from '../types';
import { ActionButtonsWrapper } from '../../components/ActionButtonsWrapper/ActionButtonsWrapper';

const valuesKey = 'sampleData.values';

export type SelectSamplesProps = AbortHandler & SetStep & SampleOrderData & SampleOrderMode;

export const SelectSamples = ({
  abortHandler,
  setStep,
  sampleOrderData,
  mode,
}: SelectSamplesProps) => {
  const { formatMessage } = useIntl();
  const { addNotification } = useNotification();

  const { watch, setValue } = useFormContext();

  const selectedSamples: SampleDataValuesSchema = watch(valuesKey);

  const toggleSample = (
    id: SampleEntry['id'],
    kindOfPattern: SampleEntry['kindOfPattern'],
    selected: boolean,
  ) => {
    const next = selected
      ? selectedSamples.filter((selectedSamplesItem) => selectedSamplesItem.id !== id)
      : selectedSamples.concat([{ id, kindOfPattern }]);

    const validationResult = sampleDataValuesSchema.safeParse(next);

    if (
      validationResult.success === false &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      validationResult.error.errors[0].message === SampleOrderErrorCodes.SampleCountTooHigh
    ) {
      addNotification({
        message: formatMessage(
          {
            defaultMessage:
              'Du kannst maximal {maximumSamples} Muster bestellen. Wähle eines ab um ein anderes auszuwählen.',
            id: 'sampleOrder.selectSamples.maxSamplesReachNotification',
          },
          { maximumSamples },
        ),
        duration: 3500,
        severity: 'warning',
      });
    } else {
      setValue(valuesKey, next, { shouldTouch: true });
    }
  };

  return (
    <SampleOrderContent
      pageContent={
        <div className="flex flex-col gap-1.5">
          {sampleOrderData.map(({ groupName, values }, indexGroup) => (
            // eslint-disable-next-line react/no-array-index-key
            <Card className="overflow-visible" key={`SelectSamplesCard${groupName}${indexGroup}`}>
              <CardHeader hasDivider className="px-2 py-1.5">
                <div className="flex flex-row justify-between">
                  <div className="text-size-h5">{groupName}</div>
                </div>
              </CardHeader>

              <CardContent className="relative overflow-visible p-2">
                <div className="flex flex-row flex-wrap gap-1.5">
                  {values.map((item, indexItem) => {
                    const selected = selectedSamples.some(
                      (selectedSamplesItem) => selectedSamplesItem.id === item.id,
                    );
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={`SelectSamplesCardContent${item.id}${indexItem}`}>
                        <SampleItem
                          {...item}
                          actionHandler={() => toggleSample(item.id, item.kindOfPattern, selected)}
                          selected={selected}
                          groupName={groupName}
                        />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      }
      actionBarContent={
        <>
          <Counter currentCount={selectedSamples.length} />
          <ActionButtonsWrapper
            primary={
              <PrimaryButton
                disabled={selectedSamples.length === 0}
                actionHandler={() => {
                  setStep(SampleOrderSteps.PersonalDataForm);
                }}
                text={formatMessage({
                  defaultMessage: 'Muster bestellen',
                  id: 'sampleOrder.selectSamples.button',
                })}
                disabledText={formatMessage({
                  defaultMessage: 'Mindestens 1 Muster wählen',
                  id: 'sampleOrder.selectSamples.button.invalid',
                })}
              />
            }
            secondary={
              mode === 'default' ? (
                <SecondaryButton
                  actionHandler={abortHandler}
                  text={formatMessage({
                    defaultMessage: 'Musterbestellung abbrechen',
                    id: 'sampleOrder.selectSamples.button.back',
                  })}
                />
              ) : null
            }
            skipPaddingTop
          />
        </>
      }
    />
  );
};
